import LegalLayout from "@/components/LegalLayout";

function H2({ children }) {
  return (
    <h2 className="mt-10 font-heading text-2xl md:text-3xl"
      style={{ fontVariationSettings: "'opsz' 64, 'wght' 500", color: "var(--ink)" }}>{children}</h2>
  );
}

function P({ children }) { return <p>{children}</p>; }

/**
 * Digital Delivery / Shipping Policy — required by Razorpay for a domestic digital SaaS
 * even though nothing physical ships. It clarifies "delivery" is instant on payment,
 * which unblocks the Razorpay merchant onboarding review.
 */
export default function Shipping() {
  return (
    <LegalLayout
      eyebrow="viii. The delivery"
      title="Digital"
      italic="delivery."
      lastUpdated="July 17, 2026"
      testid="shipping-page"
    >
      <P>
        PintaFlow is a fully digital software service (SaaS) — there is nothing physical to ship. This page clarifies how &quot;delivery&quot; works, so you and your payment provider know exactly what to expect.
      </P>

      <H2>1. What you receive</H2>
      <P>
        A PintaFlow subscription is an <strong style={{ color: "var(--ink)" }}>online workspace</strong> accessed through your web browser or Progressive Web App at{" "}
        <a href="https://pintaflow.com" style={{ color: "var(--seal)" }}>pintaflow.com</a>. There is no CD, USB, courier, or physical delivery involved.
      </P>

      <H2>2. Delivery timeline</H2>
      <P>
        Access is <strong style={{ color: "var(--ink)" }}>instant</strong>. The moment your payment is confirmed by Razorpay (or the applicable payment gateway), your account is upgraded to the paid tier — usually within seconds, and never more than 5 minutes.
      </P>
      <P>
        If your account is not upgraded within 15 minutes of a successful payment, email{" "}
        <a href="mailto:hello@pintaflow.com" style={{ color: "var(--seal)" }}>hello@pintaflow.com</a> with your payment reference — we&apos;ll fix it within the same business day.
      </P>

      <H2>3. Delivery location</H2>
      <P>
        Since delivery is digital, it happens wherever you sign in. PintaFlow is available worldwide with no geo-restrictions. Pricing is currently shown in Indian Rupees (₹); international users may see currency conversion at their payment method&apos;s prevailing rate.
      </P>

      <H2>4. Delivery confirmation</H2>
      <P>
        You will receive:
      </P>
      <ul className="list-disc pl-6 space-y-2">
        <li>An email receipt from Razorpay confirming payment.</li>
        <li>An email from <em>hello@pintaflow.com</em> confirming your new plan and billing cycle.</li>
        <li>An in-app banner and updated <strong style={{ color: "var(--ink)" }}>Settings → Billing</strong> reflecting the new plan.</li>
      </ul>

      <H2>5. Failure to access</H2>
      <P>
        If you paid but cannot access your paid features:
      </P>
      <ol className="list-decimal pl-6 space-y-2">
        <li>Sign out and sign back in — this refreshes your subscription state.</li>
        <li>Check <strong style={{ color: "var(--ink)" }}>Settings → Billing</strong> for the current plan.</li>
        <li>Still stuck? Email us with your Razorpay payment reference; we&apos;ll resolve within 1 business day, or refund immediately under our <a href="/refund" style={{ color: "var(--seal)" }}>Refund Policy</a>.</li>
      </ol>

      <H2>6. Subscription duration</H2>
      <P>
        Access continues for the entire duration of your paid plan (monthly or annual). At the end of a billing cycle, your card is charged again for the next cycle unless you cancel from <strong style={{ color: "var(--ink)" }}>Settings → Billing</strong>. Cancelling stops future billing but preserves your current cycle&apos;s access until it ends.
      </P>

      <H2>7. No physical shipping charges</H2>
      <P>
        Because nothing physical is ever shipped, there are <strong style={{ color: "var(--ink)" }}>no shipping fees, no import duties, no customs charges</strong>. The price shown at checkout is the total price.
      </P>

      <H2>8. Contact</H2>
      <P>
        Any delivery-related question, email <a href="mailto:hello@pintaflow.com" style={{ color: "var(--seal)" }}>hello@pintaflow.com</a>. See also our <a href="/refund" style={{ color: "var(--seal)" }}>Refund &amp; Cancellation Policy</a> and <a href="/terms" style={{ color: "var(--seal)" }}>Terms of Service</a>.
      </P>
    </LegalLayout>
  );
}
